<template>
  <md-card>
    <md-card-header class="md-card-header-icon md-card-header-primary">
      <div class="card-icon">
        <md-icon>videocam</md-icon>
      </div>
      <md-toolbar class="fm-card-header">
        <h4 class="md-title">Search Results</h4>
        <help-link :link="helpLink.link" title="searchResult"></help-link>
      </md-toolbar>
    </md-card-header>
    <md-card-content>
      <div class="md-layout">
        <md-field class="md-layout-item md-size-10 md-small-size-100">
          <label for="resultsPerPageSelect">Results per page</label>
          <md-select
            v-model="sort.rowsPerPage"
            @md-selected="changeSort()"
            id="resultsPerPageSelect"
          >
            <md-option class="per-page-option" value="5">5</md-option>
            <md-option class="per-page-option" value="10">10</md-option>
            <md-option class="per-page-option" value="25">25</md-option>
            <md-option class="per-page-option" value="50">50</md-option>
          </md-select>
        </md-field>
      </div>
      <span v-if="!searched"
        >Enter search criteria and click <b>Search</b> to see your
        recordings.</span
      >
      <md-table v-model="recordings" v-if="searched">
        <md-table-empty-state
          md-label="No Results"
          md-description="Adjust search criteria and try again."
        >
        </md-table-empty-state>
        <md-table-row
          slot="md-table-row"
          slot-scope="{ item }"
          class="table-striped table-hover"
        >
          <md-table-cell md-label="Channel ID">{{
            item.channelId
          }}</md-table-cell>
          <md-table-cell
            md-id="timestamp-head"
            class="timestamp-cell"
            :md-label="startTimestampTitle"
            >{{ new Date(item.startTimestamp).toLocaleString() }}</md-table-cell
          >
          <md-table-cell
            md-id="timestamp-head"
            class="timestamp-cell"
            :md-label="stopTimestampTitle"
            >{{ new Date(item.stopTimestamp).toLocaleString() }}</md-table-cell
          >
          <md-table-cell md-label="Duration (H:M:S)">{{
            timeDiff(
              new Date(item.startTimestamp),
              new Date(item.stopTimestamp)
            )
          }}</md-table-cell>
          <md-table-cell md-label="Audio" class="checkmark-column">
            <md-icon v-if="item.audioReady" class="check-icon">
              check
            </md-icon>
            <md-icon v-else class="dash-icon">
              -
            </md-icon>
          </md-table-cell>
          <md-table-cell md-label="Video" class="checkmark-column">
            <md-icon v-if="item.videoReady" class="check-icon">
              check
            </md-icon>
            <md-icon v-else class="dash-icon">
              -
            </md-icon>
          </md-table-cell>
          <md-table-cell md-label="Muxed" class="checkmark-column">
            <md-icon class="check-icon">{{ checkMuxed(item) }}</md-icon>
          </md-table-cell>
          <md-table-cell md-label="Actions">
            <md-button
              class="md-just-icon md-info md-simple"
              v-on:click="openDownloadModal(item)"
            >
              <md-icon>
                launch
              </md-icon>
              <md-tooltip v-if="item.ready" md-direction="bottom"
                >Recording Downloads</md-tooltip
              >
              <md-tooltip v-else md-direction="bottom"
                >Download the unmuxed audio and video of each
                connection</md-tooltip
              >
            </md-button>
            <md-button
              class="md-just-icon md-danger md-simple"
              v-on:click.stop="handleDelete(item)"
            >
              <md-icon>close</md-icon>
              <md-tooltip md-direction="bottom">Delete</md-tooltip>
            </md-button>
          </md-table-cell>
        </md-table-row>
      </md-table>
      <pagination v-model="sort.page" :page-count="pageCount" v-if="searched" />
    </md-card-content>
    <download-modal
      v-bind:selectedRecording="selectedRecording"
      v-bind:downloadModalVisible="downloadModalVisible"
      @closeDownloadModal="closeDownloadModal"
      ref="download"
    >
    </download-modal>
  </md-card>
</template>

<script>
import swal from "sweetalert2";
import { Pagination } from "@/components";
import { HelpLinkTemplate } from "@/fmComponents";
import helpLinks from "../../contextualHelpLinks.js";
import api from "@/api";
import DownloadModal from "./RecordingDownloadModal.vue";

export default {
  components: {
    Pagination,
    "help-link": HelpLinkTemplate,
    DownloadModal
  },
  props: ["recordings", "recordingCount"],
  data: () => ({
    helpLink: helpLinks.recordings,
    sort: {
      page: 1,
      descending: true,
      rowsPerPage: 5,
      totalItems: 100,
      sortBy: "startTimestamp"
    },
    pageCount: 2,
    downloadModalVisible: false,
    selectedRecording: null,
    searched: false
  }),
  watch: {
    sort: {
      handler() {
        this.changeSort();
      },
      deep: true,
    },
    recordingCount: {
      handler(value) {
        this.updatePageCount(value, this.sort.rowsPerPage);
        this.sort.page = 1;
      }
    }
  },
  computed: {
    startTimestampTitle: {
      get: function() {
        return `Start Timestamp`;
      }
    },
    stopTimestampTitle: {
      get: function() {
        return `Stop Timestamp`;
      }
    }
  },
  methods: {
    changedRecordingCount() {
      this.sort.totalItems = this.recordingCount;
      this.updatePageCount(this.recordingCount, this.sort.rowsPerPage);
    },
    //resetPagination is currently accessed by reference from Recordings.vue
    //TODO: we should move the search logic all into this file
    resetPagination() {
      this.changedRecordingCount();
      this.sort.page = 1;
      this.searched = true;
    },
    updatePageCount(count, rowsPerPage) {
      this.pageCount = Math.ceil(count / rowsPerPage);
    },
    changeSort() {
      this.updatePageCount(this.recordingCount, this.sort.rowsPerPage);
      this.$emit("changeSort", this.sort);
    },
    checkMuxed(item) {
      if (item.ready) {
        return "check";
      } else if (item.muxPending) {
        return "pending";
      } else {
        return "-";
      }
    },
    timeDiff(start, end) {
      const millisecondDifference = end.getTime() - start.getTime();
      // Convert milliseconds to hours, minutes and seconds, accounting for minute and second rollover
      const hours = Math.floor(millisecondDifference / 1000 / 60 / 60)
        .toString()
        .padStart(2, "0");
      const minutes = Math.floor(millisecondDifference / 1000 / 60 - hours * 60)
        .toString()
        .padStart(2, "0");
      const seconds = Math.ceil(
        millisecondDifference / 1000 - minutes * 60 - hours * 3600
      )
        .toString()
        .padStart(2, "0");
      const result = `${hours}:${minutes}:${seconds}`;
      return result;
    },
    download(link) {
      var element = document.createElement("a");
      element.setAttribute("href", link);
      element.setAttribute("download", "download");
      element.setAttribute("target", "_blank");
      element.style.display = "none";
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
    },
    handleDownload(item) {
      if (this.searched) {
        if (item.ready == true) {
          api
            .getChannelRecordingUrl(item.applicationId, item.channelId, item.id)
            .then(result => {
              this.download(result);
            })
            .catch(ex => {
              console.error(ex);
            });
        }
      }
      return;
    },
    closeDownloadModal() {
      this.downloadModalVisible = false;
    },
    openDownloadModal(item) {
      if (!item.ready) {
        //if no muxed channelRecording available, just download the connectionRecordings.
        this.$refs.download.handleConnectionsDownload(item, false);
      } else {
        //otherwise, open a modal to choose desired download.
        this.selectedRecording = item;
        this.downloadModalVisible = true;
      }
    },
    handleDelete(recordingToDelete) {
      let recordingStartTime = new Date(
        recordingToDelete.startTimestamp
      ).toLocaleString();
      let recordingDuration = this.timeDiff(
        new Date(recordingToDelete.startTimestamp),
        new Date(recordingToDelete.stopTimestamp)
      );
      swal
        .fire({
          title: "Are you sure you want to delete this recording?",
          text: `You won't be able to restore the recording from ${recordingStartTime} with a duration of ${recordingDuration}!`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonClass: "md-button md-success btn-fill",
          cancelButtonClass: "md-button md-danger btn-fill",
          confirmButtonText: "Yes, delete it!",
          buttonsStyling: false
        })
        .then(result => {
          if (result.value) {
            api
              .deleteRecording(
                recordingToDelete.applicationId,
                recordingToDelete.channelId,
                recordingToDelete.id
              )
              .then(() => {
                this.$emit("searchRetriggered", null);
                swal.fire({
                  title: "Deleted!",
                  text: `You deleted the recording from ${recordingStartTime} with a duration of ${recordingDuration}.`,
                  icon: "success",
                  confirmButtonClass: "md-button md-success btn-fill",
                  buttonsStyling: false
                });
              })
              .catch(function(ex) {
                console.error(ex);
              });
          }
        });
    }
  }
};
</script>

<style lang="scss">
// Couldn't get these to work /deep/ in scoped
@import "../../assets/scss/md/variables";

.md-select-menu.md-menu-content.md-theme-default {
  width: auto;
}

.timestamp-cell {
  &.md-table-cell {
    padding-right: 0px;
    padding-left: 8px;
  }
}
#timestamp-head {
  &.md-table-head {
    padding-right: 20px;
  }
}
.md-table-head-label {
  font-size: 16px;
  padding-right: 0px;
}
.md-card i > img {
  width: auto;
  height: 18px;
}
.swal2-popup > .swal2-actions > .md-button.md-success {
  margin: 0.3125rem 1px;
}
.md-table-cell {
  vertical-align: middle;
  &.expiry-alert {
    color: #f56969;
  }
  i {
  justify-content: right;
  }
  .md-table-cell-container {
    font-size: 13px;
    .md-icon.md-icon-font.check-icon {
      color: $brand-primary-dark;
    }
    .md-icon.md-icon-font.dash-icon {
      color: $brand-primary-dark;
      font-family: unset;
    }
  }
}
</style>
